/**
 * feat-305 — a pure, incremental Server-Sent Events parser (ADR 0001 §10.3).
 *
 * `EventSource` can't POST or send `Authorization`, so the chat client reads a
 * `fetch` body stream and feeds decoded text here. `push(chunk)` may receive any
 * split of the stream: mid-line, mid-field, between the CR and LF of a CRLF.
 * Byte-level splits inside a multi-byte UTF-8 character are handled one layer
 * up by `TextDecoder('utf-8', { stream: true })`; this parser sees text.
 *
 * Follows the WHATWG SSE parsing rules we rely on:
 *  - lines end in CRLF, LF or CR;
 *  - a line starting with ':' is a comment (the backend's `: ping` keep-alive);
 *  - `field: value` strips ONE leading space from the value;
 *  - multiple `data:` lines join with '\n';
 *  - a blank line dispatches the event; an event with no data is dropped;
 *  - an unterminated event at end-of-stream is discarded (never dispatched).
 * `id:` and `retry:` are ignored (the contract has no resume).
 */
import { ASSISTANT_EVENT_NAMES, AssistantEvent } from './types';

export interface ParsedSseEvent {
  event: string;
  data: string;
}

export interface SseParser {
  /** Feed decoded text; returns every event completed by this chunk, in order. */
  push(chunk: string): ParsedSseEvent[];
}

export function createSseParser(): SseParser {
  let buffer = '';
  // True when the previous chunk ended in '\r': a leading '\n' in the next
  // chunk is the second half of that CRLF, not an empty line.
  let pendingCR = false;
  let eventName = '';
  let dataLines: string[] = [];
  let hasData = false;

  const processLine = (line: string, out: ParsedSseEvent[]) => {
    if (line === '') {
      if (hasData) {
        out.push({ event: eventName || 'message', data: dataLines.join('\n') });
      }
      eventName = '';
      dataLines = [];
      hasData = false;
      return;
    }
    if (line.charCodeAt(0) === 58 /* ':' */) return; // comment / keep-alive
    const colon = line.indexOf(':');
    let field: string;
    let value: string;
    if (colon === -1) {
      field = line;
      value = '';
    } else {
      field = line.slice(0, colon);
      value = line.slice(colon + 1);
      if (value.charCodeAt(0) === 32 /* ' ' */) value = value.slice(1);
    }
    if (field === 'event') {
      eventName = value;
    } else if (field === 'data') {
      dataLines.push(value);
      hasData = true;
    }
    // id / retry / unknown fields: ignored.
  };

  return {
    push(chunk: string): ParsedSseEvent[] {
      const out: ParsedSseEvent[] = [];
      if (!chunk) return out;
      let text = chunk;
      if (pendingCR) {
        pendingCR = false;
        if (text.charCodeAt(0) === 10 /* '\n' */) text = text.slice(1);
      }
      buffer += text;
      let start = 0;
      for (let i = 0; i < buffer.length; i++) {
        const c = buffer.charCodeAt(i);
        if (c === 10 /* \n */ || c === 13 /* \r */) {
          processLine(buffer.slice(start, i), out);
          if (c === 13) {
            if (i + 1 < buffer.length) {
              if (buffer.charCodeAt(i + 1) === 10) i++;
            } else {
              pendingCR = true;
            }
          }
          start = i + 1;
        }
      }
      buffer = buffer.slice(start);
      return out;
    },
  };
}

/**
 * Turn a parsed SSE event into a typed contract event. Unknown event names
 * (a future `proposal`, say) and malformed JSON return null, so an old client
 * ignores what it doesn't understand (ADR §1.6 "Scorecard plug-in check").
 */
export function toAssistantEvent(parsed: ParsedSseEvent): AssistantEvent | null {
  if (!(ASSISTANT_EVENT_NAMES as ReadonlyArray<string>).includes(parsed.event)) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(parsed.data);
  } catch {
    return null;
  }
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
  return { ...(payload as object), type: parsed.event } as AssistantEvent;
}
